import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Booking } from '../../../core/entities/booking/booking.entity';
import { DateTime } from 'luxon';
import { TimeSlot } from '../../../creator/services/calendar/calendar.service';
import {mapBooking, mapBookings} from '../../../creator/services/booking/booking.service';

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Create a booking for a service.
   */
  public createBooking(
    serviceId: number,
    customerEmail: string,
    customerFirstname: string | null,
    customerLastname: string | null,
    price: number | null,
    note: string | null,
    datetime: DateTime,
    deposit: number | null = null,
  ): Observable<{ booking: Booking; checkoutUrl: string }> {
    const datetimeUtcIso = datetime.toUTC().toISO();

    return this.checkAvailability(serviceId, datetime).pipe(
      switchMap(response => {
        if (!response.isAvailable) {
          return throwError(() => new Error('Selected time is not available'));
        }
        const payload: Record<string, unknown> = {
          serviceId,
          customerEmail,
          customerFirstname,
          customerLastname,
          note,
          price,
          datetime: datetimeUtcIso,
        };

        if (typeof deposit === 'number') {
          payload['deposit'] = Math.round(deposit);
        }

        return this.http.post<{ booking: any; checkoutUrl: string }>(
          `${environment.apiUrl}/api/v1/marketplace/booking`,
          payload
        ).pipe(
          map(res => ({
            booking: mapBooking(res.booking),
            checkoutUrl: res.checkoutUrl
          }))
        );
      })
    );
  }

  public checkAvailability(
    serviceId: number,
    datetime: DateTime
  ): Observable<{ datetime: string; isAvailable: boolean }> {
    const datetimeUtcIso = datetime.toUTC().toISO() ?? '';
    return this.http.get<{ datetime: string; isAvailable: boolean }>(
      `${environment.apiUrl}/api/v1/marketplace/calendar/service/${serviceId}/check-availability?datetime=${encodeURIComponent(datetimeUtcIso)}`
    );
  }

  public getAvailableTimeSlots(
    serviceId: number,
    date: Date | DateTime
  ): Observable<{ date: string; weekday: string; timeSlots: TimeSlot[] }> {
    let dateString: string;
    if (date instanceof DateTime) {
      dateString = date.toISODate() ?? '';
    } else {
      dateString = date.toISOString().split('T')[0];
    }
    return this.http.get<{ date: string; weekday: string; timeSlots: TimeSlot[] }>(
      `${environment.apiUrl}/api/v1/marketplace/calendar/service/${serviceId}/available-slots?date=${dateString}`
    );
  }

  /**
   * Get available days for a service in a given month (format: 'YYYY-MM').
   */
  public getAvailableDaysForMonth(
    serviceId: number,
    month: string,
  ): Observable<string[]> {
    return this.http.get<{ availableDays: string[] }>(
      `${environment.apiUrl}/api/v1/marketplace/calendar/service/${serviceId}/available-days?month=${month}`
    ).pipe(
      map(response => response.availableDays)
    );
  }

  public getBooking(id: number): Observable<Booking> {
    return this.http.get<any>(`${environment.apiUrl}/api/v1/marketplace/booking/${id}`)
      .pipe(map(mapBooking));
  }

  public getBillingInfos(
    bookingId: number,
    email: string,
  ): Observable<{
    paymentDate: string|null;
    status: string;
    datetime: string;
    publicPrice: number;
    discount: number|null;
    depositAmount: number|null;
    invoiceUrl: string|null;
  }> {
    return this.http.get<{
      paymentDate: string|null;
      status: string;
      datetime: string;
      publicPrice: number;
      discount: number|null;
      depositAmount: number|null;
      invoiceUrl: string|null;
    }>(
      `${environment.apiUrl}/api/v1/marketplace/booking/${bookingId}/billing-infos`,
      { params: { email } }
    );
  }

  public getIncomingBookings(): Observable<Booking[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/v1/marketplace/booking/incoming`)
      .pipe(map(mapBookings));
  }

  public getPastBookings(): Observable<Booking[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/v1/marketplace/booking/past`)
      .pipe(map(mapBookings));
  }

  public payRemainingBalance(bookingId: number): Observable<{ checkoutUrl: string }> {
    return this.http.post<{ checkoutUrl: string }>(
      `${environment.apiUrl}/api/v1/marketplace/booking/${bookingId}/pay-balance`,
      {}
    );
  }

  public downloadBookingPhotosAsBlob(bookingId: number): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/api/v1/marketplace/booking/${bookingId}/photos`, {
      responseType: 'blob',
    });
  }
}
